import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Button from '../atoms/Button';
import { TimerMode } from './TimerCard';
import { FaHourglassHalf, FaStopwatch } from 'react-icons/fa';
import { EffectType } from '../atoms/TimerEffect';

interface TimerFormProps {
  onSubmit: (timerData: {
    name: string;
    hours: number;
    minutes: number;
    seconds: number;
    mode: TimerMode;
    showMilliseconds: boolean;
    effectType: EffectType;
    effectOpacity: number;
    effectColor?: string;
    effectSecondaryColor?: string;
    effectSpeed: number;
  }) => void;
  onCancel?: () => void;
  initialValues?: {
    name?: string;
    hours?: number;
    minutes?: number;
    seconds?: number;
    mode?: TimerMode;
    showMilliseconds?: boolean;
    effectType?: EffectType;
    effectOpacity?: number;
    effectColor?: string;
    effectSecondaryColor?: string;
    effectSpeed?: number;
  };
  isEditing?: boolean;
  inFullscreen?: boolean;
}

const TimerForm: React.FC<TimerFormProps> = ({
  onSubmit,
  onCancel,
  initialValues = {},
  isEditing = false,
  inFullscreen = false
}) => {
  const { t } = useTranslation();
  
  // 生成默认计时器名称
  const generateDefaultName = () => {
    const currentDate = new Date();
    const formattedDate = `${currentDate.getMonth() + 1}/${currentDate.getDate()}`;
    return `${t('timer')} ${formattedDate}`;
  };

  // 表单状态
  const [name, setName] = useState(initialValues.name || '');
  const [hours, setHours] = useState(initialValues.hours || 0);
  const [minutes, setMinutes] = useState(initialValues.minutes || 0);
  const [seconds, setSeconds] = useState(initialValues.seconds || 0);
  const [mode, setMode] = useState<TimerMode>(initialValues.mode || 'countdown');
  const [showMilliseconds, setShowMilliseconds] = useState(initialValues.showMilliseconds || false);
  const [effectType, setEffectType] = useState<EffectType>(initialValues.effectType || 'none');
  const [effectOpacity, setEffectOpacity] = useState(initialValues.effectOpacity || 0.5);
  const [effectColor, setEffectColor] = useState(initialValues.effectColor || '#000000');
  const [effectSecondaryColor, setEffectSecondaryColor] = useState(initialValues.effectSecondaryColor || '#ffffff');
  const [effectSpeed, setEffectSpeed] = useState(initialValues.effectSpeed || 1);
  
  // 为输入框保留默认名称占位符
  const [defaultName] = useState(generateDefaultName());
  
  // 表单验证
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // 处理表单提交
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // 验证表单
    const newErrors: Record<string, string> = {};
    
    // 使用默认名称或用户输入名称
    const timerName = name.trim() || defaultName;
    
    if (mode === 'countdown' && hours === 0 && minutes === 0 && seconds === 0) {
      newErrors.time = t('countdown') + ' ' + t('duration');
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // 提交表单
    onSubmit({
      name: timerName,
      hours,
      minutes,
      seconds,
      mode,
      showMilliseconds,
      effectType,
      effectOpacity,
      effectColor,
      effectSecondaryColor,
      effectSpeed
    });
    
    // 如果不是编辑模式，重置表单
    if (!isEditing) {
    setName('');
    setHours(0);
    setMinutes(0);
    setSeconds(0);
    setMode('countdown');
    setShowMilliseconds(false);
      setEffectType('none');
      setEffectOpacity(0.5);
      setEffectColor('#000000');
      setEffectSecondaryColor('#ffffff');
      setEffectSpeed(1);
    }
    
    setErrors({});
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 ${inFullscreen ? 'max-h-[90vh] overflow-y-auto' : ''}`}
    >
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">
          {isEditing ? t('editTimer') : t('createTimer')}
        </h2>
        {inFullscreen && (
          <div className="text-sm text-gray-500 dark:text-gray-400 italic">
            {t('fullscreen')} {t('mode')}
          </div>
        )}
      </div>
      
      <form onSubmit={handleSubmit}>
        {/* 计时器名称 */}
        <div className="mb-4">
          <label htmlFor="timer-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('timerName')}
          </label>
          <input
            id="timer-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white"
            placeholder={defaultName}
          />
          {errors.name && <p className="mt-1 text-sm text-danger">{errors.name}</p>}
        </div>
        
        {/* 计时器模式 - 增强版 */}
        <div className="mb-4">
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('timerMode')}
          </span>
          <div className="flex gap-4">
            <motion.label 
              className={`relative flex-1 flex flex-col items-center justify-center p-4 rounded-lg cursor-pointer border-2 transition-all duration-200 ${mode === 'countdown' 
                ? 'border-primary bg-primary/10 dark:bg-primary/20' 
                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <input
                type="radio"
                className="sr-only"
                name="timer-mode"
                checked={mode === 'countdown'}
                onChange={() => setMode('countdown')}
              />
              <motion.div 
                initial={{ scale: 1 }}
                animate={{ 
                  scale: mode === 'countdown' ? [1, 1.2, 1] : 1,
                  rotate: mode === 'countdown' ? [0, 0, 0] : 0
                }}
                transition={{ duration: 0.3 }}
                className="text-3xl mb-2 text-primary"
              >
                <FaHourglassHalf />
              </motion.div>
              <span className={`font-medium ${mode === 'countdown' ? 'text-primary' : ''}`}>
                {t('countdown')}
              </span>
              {mode === 'countdown' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-1 -right-1 bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                >
                  ✓
                </motion.div>
              )}
            </motion.label>
            
            <motion.label 
              className={`relative flex-1 flex flex-col items-center justify-center p-4 rounded-lg cursor-pointer border-2 transition-all duration-200 ${mode === 'stopwatch' 
                ? 'border-accent bg-accent/10 dark:bg-accent/20' 
                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <input
                type="radio"
                className="sr-only"
                name="timer-mode"
                checked={mode === 'stopwatch'}
                onChange={() => setMode('stopwatch')}
              />
              <motion.div 
                initial={{ scale: 1 }}
                animate={{ 
                  scale: mode === 'stopwatch' ? [1, 1.2, 1] : 1,
                  rotate: mode === 'stopwatch' ? [0, 15, 0, -15, 0] : 0
                }}
                transition={{ duration: 0.5 }}
                className="text-3xl mb-2 text-accent"
              >
                <FaStopwatch />
              </motion.div>
              <span className={`font-medium ${mode === 'stopwatch' ? 'text-accent' : ''}`}>
                {t('stopwatch')}
              </span>
              {mode === 'stopwatch' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-1 -right-1 bg-accent text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                >
                  ✓
                </motion.div>
              )}
            </motion.label>
          </div>
        </div>
        
        {/* 时间设置 */}
        <div className="mb-4">
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {mode === 'countdown' ? t('duration') : `${t('duration')} (${t('optional')})`}
          </span>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label htmlFor="timer-hours" className="block text-xs text-gray-500 dark:text-gray-400">
                {t('hours')}
              </label>
              <input
                id="timer-hours"
                type="number"
                min="0"
                max="99"
                value={hours}
                onChange={(e) => setHours(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="timer-minutes" className="block text-xs text-gray-500 dark:text-gray-400">
                {t('minutes')}
              </label>
              <input
                id="timer-minutes"
                type="number"
                min="0"
                max="59"
                value={minutes}
                onChange={(e) => setMinutes(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="timer-seconds" className="block text-xs text-gray-500 dark:text-gray-400">
                {t('seconds')}
              </label>
              <input
                id="timer-seconds"
                type="number"
                min="0"
                max="59"
                value={seconds}
                onChange={(e) => setSeconds(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          {errors.time && <p className="mt-1 text-sm text-danger">{errors.time}</p>}
        </div>
        
        {/* 显示毫秒 */}
        <div className="mb-6">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              className="form-checkbox text-primary focus:ring-primary rounded"
              checked={showMilliseconds}
              onChange={(e) => setShowMilliseconds(e.target.checked)}
            />
            <span className="ml-2">{t('showMilliseconds')}</span>
          </label>
        </div>
        
        {/* 特效设置 */}
        <div className="mb-4">
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('effect')}
          </span>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <motion.label 
              className={`relative flex-1 flex flex-col items-center justify-center p-4 rounded-lg cursor-pointer border-2 transition-all duration-200 ${effectType === 'none' 
                ? 'border-gray-500 bg-gray-100 dark:bg-gray-700' 
                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <input
                type="radio"
                className="sr-only"
                name="effect-type"
                checked={effectType === 'none'}
                onChange={() => setEffectType('none')}
              />
              <motion.div 
                initial={{ scale: 1 }}
                animate={{ 
                  scale: effectType === 'none' ? [1, 1.2, 1] : 1,
                  rotate: effectType === 'none' ? [0, 0, 0] : 0
                }}
                transition={{ duration: 0.3 }}
                className="text-3xl mb-2 text-gray-500"
              >
                {t('none')}
              </motion.div>
              {effectType === 'none' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-1 -right-1 bg-gray-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                >
                  ✓
                </motion.div>
              )}
            </motion.label>
            
            <motion.label 
              className={`relative flex-1 flex flex-col items-center justify-center p-4 rounded-lg cursor-pointer border-2 transition-all duration-200 ${effectType === 'circle' 
                ? 'border-primary bg-primary/10 dark:bg-primary/20' 
                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <input
                type="radio"
                className="sr-only"
                name="effect-type"
                checked={effectType === 'circle'}
                onChange={() => setEffectType('circle')}
              />
              <motion.div 
                initial={{ scale: 1 }}
                animate={{ 
                  scale: effectType === 'circle' ? [1, 1.2, 1] : 1,
                  rotate: effectType === 'circle' ? [0, 0, 0] : 0
                }}
                transition={{ duration: 0.3 }}
                className="text-3xl mb-2 text-primary"
              >
                {t('circle')}
              </motion.div>
              {effectType === 'circle' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-1 -right-1 bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                >
                  ✓
                </motion.div>
              )}
            </motion.label>
            
            <motion.label 
              className={`relative flex-1 flex flex-col items-center justify-center p-4 rounded-lg cursor-pointer border-2 transition-all duration-200 ${effectType === 'wave' 
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <input
                type="radio"
                className="sr-only"
                name="effect-type"
                checked={effectType === 'wave'}
                onChange={() => setEffectType('wave')}
              />
              <motion.div 
                initial={{ scale: 1 }}
                animate={{ 
                  scale: effectType === 'wave' ? [1, 1.2, 1] : 1
                }}
                transition={{ duration: 0.3 }}
                className="text-3xl mb-2 text-blue-500"
              >
                {t('wave')}
              </motion.div>
              {effectType === 'wave' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-1 -right-1 bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                >
                  ✓
                </motion.div>
              )}
            </motion.label>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <motion.label 
              className={`relative flex-1 flex flex-col items-center justify-center p-4 rounded-lg cursor-pointer border-2 transition-all duration-200 ${effectType === 'pulse' 
                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <input
                type="radio"
                className="sr-only"
                name="effect-type"
                checked={effectType === 'pulse'}
                onChange={() => setEffectType('pulse')}
              />
              <motion.div 
                initial={{ scale: 1 }}
                animate={{ 
                  scale: effectType === 'pulse' ? [1, 1.2, 1] : 1
                }}
                transition={{ duration: 0.3 }}
                className="text-3xl mb-2 text-purple-500"
              >
                {t('pulse')}
              </motion.div>
              {effectType === 'pulse' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-1 -right-1 bg-purple-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                >
                  ✓
                </motion.div>
              )}
            </motion.label>
            
            <motion.label 
              className={`relative flex-1 flex flex-col items-center justify-center p-4 rounded-lg cursor-pointer border-2 transition-all duration-200 ${effectType === 'particles' 
                ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <input
                type="radio"
                className="sr-only"
                name="effect-type"
                checked={effectType === 'particles'}
                onChange={() => setEffectType('particles')}
              />
              <motion.div 
                initial={{ scale: 1 }}
                animate={{ 
                  scale: effectType === 'particles' ? [1, 1.2, 1] : 1
                }}
                transition={{ duration: 0.3 }}
                className="text-3xl mb-2 text-green-500"
              >
                {t('particles')}
              </motion.div>
              {effectType === 'particles' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                >
                  ✓
                </motion.div>
              )}
            </motion.label>
            
            <motion.label 
              className={`relative flex-1 flex flex-col items-center justify-center p-4 rounded-lg cursor-pointer border-2 transition-all duration-200 ${effectType === 'gradient' 
                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' 
                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <input
                type="radio"
                className="sr-only"
                name="effect-type"
                checked={effectType === 'gradient'}
                onChange={() => setEffectType('gradient')}
              />
              <motion.div 
                initial={{ scale: 1 }}
                animate={{ 
                  scale: effectType === 'gradient' ? [1, 1.2, 1] : 1
                }}
                transition={{ duration: 0.3 }}
                className="text-3xl mb-2 text-amber-500"
              >
                {t('gradient')}
              </motion.div>
              {effectType === 'gradient' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-1 -right-1 bg-amber-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                >
                  ✓
                </motion.div>
              )}
            </motion.label>
          </div>
        </div>
        
        {/* 特效参数 */}
        {effectType !== 'none' && (
          <>
            <div className="mb-4">
              <label htmlFor="effect-opacity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('opacity')}: {Math.round(effectOpacity * 100)}%
              </label>
              <input
                id="effect-opacity"
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={effectOpacity}
                onChange={(e) => setEffectOpacity(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="effect-speed" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('speed')}: {effectSpeed.toFixed(1)}x
              </label>
              <input
                id="effect-speed"
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={effectSpeed}
                onChange={(e) => setEffectSpeed(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="effect-color" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('primaryColor')}
                </label>
                <input
                  id="effect-color"
                  type="color"
                  value={effectColor || (mode === 'countdown' ? '#4A6FA5' : '#FF6B6B')}
                  onChange={(e) => setEffectColor(e.target.value)}
                  className="w-full h-10 rounded"
                />
              </div>
              
              <div>
                <label htmlFor="effect-secondary-color" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('secondaryColor')}
                </label>
                <input
                  id="effect-secondary-color"
                  type="color"
                  value={effectSecondaryColor || (mode === 'countdown' ? '#87A7D4' : '#FFAEAE')}
                  onChange={(e) => setEffectSecondaryColor(e.target.value)}
                  className="w-full h-10 rounded"
                />
              </div>
            </div>
          </>
        )}
        
        {/* 按钮区域 */}
        <div className={`flex gap-2 ${inFullscreen ? 'mt-8 justify-center' : 'justify-end'}`}>
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className={inFullscreen ? 'px-8 py-3 text-lg' : ''}
            >
              {t('cancel')}
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            className={inFullscreen ? 'px-8 py-3 text-lg' : ''}
          >
            {isEditing ? t('save') : t('createTimer')}
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

export default TimerForm;